import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { TaskyButton, TaskyInput, TaskyFormGroup } from '../tasky';
import { useMetaLang, AUTH_STRINGS } from '../../i18n/metaReviewLang';

interface LoginFormProps {
  onSubmit: (data: { email: string; password: string }) => void;
  isLoading?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit, isLoading = false }) => {
  const [lang] = useMetaLang();
  const t = AUTH_STRINGS[lang];
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const validate = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};
    if (!email) {
      newErrors.email = t.errEmailRequired;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t.errEmailInvalid;
    }
    if (!password) {
      newErrors.password = t.errPasswordRequired;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({ email, password });
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <TaskyFormGroup label={t.email} required htmlFor="email" error={errors.email}>
        <TaskyInput
          id="email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoComplete="email"
          required
          hasError={Boolean(errors.email)}
        />
      </TaskyFormGroup>

      <TaskyFormGroup
        label={
          <span className="flex items-center justify-between w-full">
            <span>
              {t.password} <span className="text-destructive">*</span>
            </span>
            <Link
              to="/forgot-password"
              className="text-xs font-normal text-primary hover:text-primary-hover transition-colors"
            >
              {t.forgotPasswordShort}
            </Link>
          </span>
        }
        htmlFor="password"
        error={errors.password}
      >
        <div className="relative">
          <TaskyInput
            id="password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className="pr-10"
            hasError={Boolean(errors.password)}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </TaskyFormGroup>

      <TaskyButton type="submit" isLoading={isLoading} className="w-full">
        {isLoading ? t.loggingIn : t.login}
      </TaskyButton>
    </form>
  );
};
